import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

type ModalView = 'login' | 'register' | 'forgot' | 'register-success';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  mobileRedirect?: string;
}

export default function LoginModal({ isOpen, onClose, mobileRedirect }: LoginModalProps) {
  const [view, setView] = useState<ModalView>('login');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const resetForm = () => {
    setEmail('');
    setFullName('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccessMessage('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const switchView = (newView: ModalView) => {
    resetForm();
    setView(newView);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError('Invalid email or password');
        return;
      }

      if (data.session) {
        toast.success('Logged in successfully');
        navigate(mobileRedirect || '/dashboard');
        onClose();
      }
    } catch {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) {
      setError('Full name is required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }

    setLoading(true);

    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName.trim(),
            role: 'user',
          }
        }
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          setError('An account with this email already exists');
        } else {
          setError(signUpError.message);
        }
        return;
      }

      switchView('register-success');
    } catch {
      setError('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/dashboard`,
      });

      if (resetError) {
        setError(resetError.message);
        return;
      }

      setSuccessMessage('Password reset instructions have been sent to your email.');
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    setView('login');
    onClose();
  };

  const titles: Record<ModalView, string> = {
    login: 'Admin Login',
    register: 'Create Account',
    forgot: 'Reset Password',
    'register-success': 'Account Created',
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform rounded-2xl bg-white shadow-xl transition-all">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-full text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="p-8">
                  <Dialog.Title as="h3" className="text-2xl font-display font-bold text-golf-gray mb-6 text-center">
                    {titles[view]}
                  </Dialog.Title>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-600 mb-4">
                      <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                      <p className="text-sm">{error}</p>
                    </div>
                  )}

                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 text-green-700 mb-4">
                      <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                      <p className="text-sm">{successMessage}</p>
                    </div>
                  )}

                  {view === 'login' && (
                    <form onSubmit={handleLogin} className="space-y-5">
                      <div>
                        <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="login-email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                          required
                          disabled={loading}
                          placeholder="admin@example.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            id="login-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange pr-10"
                            required
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => switchView('forgot')}
                          className="text-sm text-golf-orange hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>

                      <button
                        type="submit"
                        className="w-full modern-button py-3 text-white font-medium disabled:opacity-50"
                        disabled={loading}
                      >
                        {loading ? 'Logging in...' : 'Login'}
                      </button>

                      <p className="text-center text-sm text-gray-500">
                        Don't have an account?{' '}
                        <button
                          type="button"
                          onClick={() => switchView('register')}
                          className="text-golf-orange font-medium hover:underline"
                        >
                          Register
                        </button>
                      </p>
                    </form>
                  )}

                  {view === 'register' && (
                    <form onSubmit={handleRegister} className="space-y-5">
                      <div>
                        <label htmlFor="reg-full-name" className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="reg-full-name"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                          required
                          disabled={loading}
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="reg-email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                          required
                          disabled={loading}
                          placeholder="you@example.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 mb-1">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            id="reg-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange pr-10"
                            required
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Min. 8 characters, one uppercase letter and one number</p>
                      </div>

                      <div>
                        <label htmlFor="reg-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="reg-confirm-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange pr-10"
                            required
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                          >
                            {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full modern-button py-3 text-white font-medium disabled:opacity-50"
                        disabled={loading}
                      >
                        {loading ? 'Creating account...' : 'Create Account'}
                      </button>

                      <p className="text-center text-sm text-gray-500">
                        Already have an account?{' '}
                        <button
                          type="button"
                          onClick={() => switchView('login')}
                          className="text-golf-orange font-medium hover:underline"
                        >
                          Login
                        </button>
                      </p>
                    </form>
                  )}

                  {view === 'register-success' && (
                    <div className="text-center space-y-5">
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                        <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-golf-gray mb-2">Awaiting Approval</h4>
                        <p className="text-sm text-gray-500 leading-relaxed">
                          Your account has been created successfully. An administrator needs to approve your access before you can log in to the portal. You will be notified once your account is activated.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => switchView('login')}
                        className="w-full modern-button py-3 text-white font-medium"
                      >
                        Back to Login
                      </button>
                    </div>
                  )}

                  {view === 'forgot' && (
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <p className="text-sm text-gray-600 -mt-2">
                        Enter your email address and we'll send you instructions to reset your password.
                      </p>

                      <div>
                        <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="forgot-email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                          required
                          disabled={loading || !!successMessage}
                          placeholder="admin@example.com"
                        />
                      </div>

                      {!successMessage && (
                        <button
                          type="submit"
                          className="w-full modern-button py-3 text-white font-medium disabled:opacity-50"
                          disabled={loading}
                        >
                          {loading ? 'Sending...' : 'Send Reset Instructions'}
                        </button>
                      )}

                      <p className="text-center text-sm text-gray-500">
                        <button
                          type="button"
                          onClick={() => switchView('login')}
                          className="text-golf-orange font-medium hover:underline"
                        >
                          Back to Login
                        </button>
                      </p>
                    </form>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
